import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { useNavigate, useLocation } from "react-router-dom";
import { usePermission } from "../../../context/PermissionContext";
import { useAuth } from "../../../context/AuthProvider";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import Messages from "../../../constants/Messages";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import DeleteModal from "../../../constants/DeleteModal";
import InvoiceTemp from "../purchase/PurchaseView";
import AddSale from "./AddSale";
import TemplateModalLayout from "../../../templates/modules/TemplateModalLayout";
import { RefreshIcon } from "../../../constants/Icons";

const ListReturnSale = (isActive) => {
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEstimateData, setEstimateData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  // const [estimate, setEstimation] = useState([]);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { isUser } = useAuth();
  const { permission, hasPermission, globelPopupOpen } = usePermission();
  const navigate = useNavigate();
  const location = useLocation(); // Access location to get state
  const [selectedReturnOption, setSelectedReturnOption] = useState("RETURN");

  const handleListEstimate = async (page = 0) => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const data = { pageno: page + 1, limit: perPage };
      const response = await AxiosInstance.get(
        `module/salereturn/all?pageno=${page + 1}&limit=${perPage}`,
      );
      const results = response.data.results || [];
      setEstimateData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await handleListEstimate(currentPage);
  };

  const handleGetEstimation = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/salereturn/${saleId}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleView = async (item) => {
    const data = await handleGetEstimation(item._id);
    setViewData(data);
    setViewModalOpen(true);
  };

  // const handleClose = () => {
  //   setViewModalOpen(false);
  //   setViewData(null);
  //   // Clear the location state to prevent re-opening the modal
  //   navigate(location.pathname, { replace: true, state: {} });
  // };

  const openDeleteModal = (cItem) => {
    setDeleteData(cItem);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.delete(
        `module/salereturn/${isDeleteData?._id}`,
        // {
        //   data: {
        //     estimateid: isDeleteData?._id,
        //   },
        // }
      );
      showAlert("success", Messages.SUCCESS.ESTIMATE_DELETED);

      const newTotal = totalItems - 1;
      const newTotalPages = Math.ceil(newTotal / perPage);

      if (isEstimateData.length === 1 && currentPage > 0) {
        setCurrentPage((prev) => prev - 1);
        await handleListEstimate(currentPage - 1);
      } else {
        await handleListEstimate(currentPage);
      }

      setTotalItems(newTotal);
      setTotalPages(newTotalPages);
      setDeleteModalOpen(false);

      closeDeleteModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteData(null);
    setDeleteModalOpen(false);
  };

  // const getColor = (status) => {
  //   switch (status?.toLowerCase()) {
  //     case "active":
  //       return "bg-label-success";
  //     case "cancelled":
  //       return "bg-label-danger";
  //     default:
  //       return "bg-label-secondary";
  //   }
  // };

  const getBgColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-honey";
      case "invoice":
        return "bg-label-moon";
      default:
        return "bg-label-secondary";
    }
  };

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const getBgTxtColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-wave";
      case "invoice":
        return "bg-label-deep-purple";
      default:
        return "bg-label-secondary";
    }
  };

  const getColor = (saleTrack) => {
    switch (saleTrack?.toUpperCase()) {
      case "ADDED":
        return "bg-label-success"; // Green
      case "UPDATED":
        return "bg-label-warning"; // Yellow
      case "DELETED":
        return "bg-label-danger"; // Red
      case "RETURNED":
        return "bg-label-orange";
      default:
        return "bg-label-secondary"; // Default color
    }
  };

  const getStatusLabel = (saleTrack) => {
    switch (saleTrack?.toUpperCase()) {
      case "ADDED":
        return "Original";
      case "UPDATED":
        return "Modified";
      case "DELETED":
        return "Cancelled";
      case "RETURNED":
        return "Returned";
      default:
        return "Unknown";
    }
  };

  const handleReturnClose = () => {
    setViewModalOpen(false);
  };

  useKeyboardTableNavigation({
    modals: {
      isDeleteModalOpen,
      isViewModalOpen,
      globelPopupOpen,
    },
    onEnter: handleView,
  });

  useEffect(() => {
    handleListEstimate(currentPage);
  }, [currentPage]);

  useEffect(() => {
    handleListEstimate(0);
    setCurrentPage(0);
  }, []);

  useEffect(() => {
    if (location.state?.modalData) {
      setViewData(location.state.modalData);
      setViewModalOpen(true);
    }
  }, [location.state]);

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="page-breadcrumb d-flex align-items-center mb-3">
          <div className="pb-1">
            <button
              type="button"
              className="btn btn-sm btn-light p-1 shadow-none"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Refresh list"
            >
              <RefreshIcon iClass={`${isLoading ? "bx-spin" : ""}`}/>
            </button>
          </div>
          <div className="breadcrumb-title pe-3">List Return Sale</div>
          {/* <div className="ms-auto">
            <button
              type="button"
              className="btn btn-primary mt-2 mt-lg-0"
              onClick={handleLink}
            >
              <i className="ng ng-ng-add-icon"></i> Add Estimate
            </button>
          </div> */}
        </div>

        <div className="row">
          <div className="col">
            <div className="card radius-10 mb-0">
              <div className="card-body px-0">
                <div className="row px-3">
                  <div className="col-lg-2 col-6 d-flex align-items-center">
                    <div>
                      <strong>Total Return Sale:</strong>{" "}
                      <span className="badge btn-label-primary fs-15 ">
                        {totalItems || 0}
                      </span>
                    </div>
                  </div>

                  <div className="col-lg-2 col-12 mb-2 mb-lg-0">
                    {/* <div className="input-group">
                      <span className="input-group-text">
                        <i className="bx bx-sort-down"></i>
                      </span>
                      <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="DRAFT">Draft</option>
                        <option value="SUBMITTED">Submitted</option>
                      </select>
                    </div> */}
                  </div>
                  {/* <div className="col-lg-8 col-6 d-flex justify-content-end ">
                    <button
                      type="button"
                      className="btn btn-primary mt-2 mt-lg-0"
                      onClick={() => handleAddSale()}
                    >
                      <i className="ng ng-ng-add-icon"></i> Add Purchase
                    </button>
                  </div> */}
                </div>

                <Table
                  loading={isLoading}
                  data={isEstimateData}
                  onRowClick={(item) => handleView(item)}
                  columns={[
                    {
                      label: "S.No",
                      render: (_, index) => (
                        <>{currentPage * perPage + index + 1}</>
                      ),
                    },
                    {
                      label: "Sale Mode",
                      render: (item) => (
                        <span
                          className={`badge rounded-pill ${getBgColor(item?.salemode)}`}
                        >
                          {formatTrackMode(item?.salemode)}
                        </span>
                      ),
                    },
                    {
                      label: "Return No",
                      render: (item) => (
                        <span
                          className={`badge rounded-pill ${getBgTxtColor(
                            item?.salemode,
                          )}`}
                        >
                          {item?.saleReturnNo}
                        </span>
                      ),
                    },
                    {
                      label: "Return Date",
                      render: (item) => (
                        <span className="text-heading fw-medium text-truncate">
                          {
                            DateConverter.ISOToDisplayFormat(
                              DateConverter.convertToTimeZone(
                                item?.saleReturnDate,
                                isUser?.timezone,
                              ),
                            ).date
                          }
                        </span>
                      ),
                    },
                    {
                      label: "Customer",
                      render: (item) => (
                        <div className="d-flex flex-column">
                          <span className="text-nowrap text-heading fw-medium">
                            {item.CustomerInfo
                              ? `${item?.CustomerInfo?.name}`
                              : ""}
                          </span>
                          <span className="text-nowrap">
                            <small>
                              {item.CustomerInfo
                                ? `${item?.CustomerInfo?.phone}`
                                : " - "}
                            </small>
                          </span>
                        </div>
                      ),
                    },
                    {
                      label: "Return Items",
                      render: (item) => (
                        <>{item?.totalReturnItemCount || 0} Item(s)</>
                      ),
                    },
                    {
                      label: "Grand Total",
                      render: (item) => (
                        <span className="text-heading fw-medium text-truncate">
                          ₹
                          {CurrencyConverter.toCurrency(
                            Number(item.returnGrandTotalRounded) || 0,
                            "INR",
                          )}
                        </span>
                      ),
                    },
                    {
                      label: "Status",
                      render: (item) => (
                        <span
                          className={`badge rounded-pill ${getColor(
                            item.returnTrack,
                          )}`}
                        >
                          {getStatusLabel(item.returnTrack)}
                        </span>
                      ),
                    },
                    // {
                    //   label: "Actions",
                    //   render: (item) => (
                    //     <div className="order-actions opacity-0 opacity-md-100">
                    //       {/* <button
                    //               type="button"
                    //               className="btn-label-info shadow-none me-2"
                    //               onClick={(e) => {
                    //                       e.stopPropagation();
                    //                        handleView(item)}}
                    //             >
                    //               <i className="bx bx-show"></i>
                    //             </button> */}
                    //       {/* {item?.status === "DRAFT" && (
                    //         <>
                    //           <button
                    //             type="button"
                    //             className="btn-label-primary shadow-none me-2"
                    //             // onClick={(e) => {
                    //             //   e.stopPropagation();
                    //             //   handleEdit(item);
                    //             // }}
                    //           >
                    //             <i className="ng ng-ng-edit-icon"></i>
                    //           </button>
                    //           <button
                    //             type="button"
                    //             className="btn-label-danger shadow-none me-2"
                    //             onClick={(e) => {
                    //               e.stopPropagation();
                    //               openDeleteModal(item);
                    //             }}
                    //           >
                    //             <i className="ng ng-ng-delete-icon"></i>
                    //           </button>
                    //         </>
                    //       )} */}
                    //     </div>
                    //   ),
                    // },
                  ]}
                />
                {totalPages > 0 && (
                  <div className="d-flex justify-content-center mt-3">
                    <ReactPaginate
                      previousLabel={"← Previous"}
                      nextLabel={"Next →"}
                      breakLabel={"..."}
                      onPageChange={({ selected }) => {
                        setIsLoading(true);
                        setCurrentPage(selected);
                      }}
                      pageRangeDisplayed={5}
                      pageCount={totalPages}
                      containerClassName="pagination"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                      activeClassName="active"
                      forcePage={currentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <TemplateModalLayout
          isOpen={isViewModalOpen}
          isData={isViewData}
          onClose={handleReturnClose}
          selectedOption={selectedReturnOption}
          setSelectedOption={setSelectedReturnOption}
          setSaleData={setViewData}
          // isReturnViews={true} 
          isActive={isActive}
        />        
        {/* 
        {isViewModalOpen && (
          <InvoiceTemp
            data={isViewData}
            onClose={handleClose}
            onEdit={handleEdit}
            onDone={handleDone}
          />
        )} */}

        {/* {isDeleteModalOpen && (
          <DeleteModal
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleDelete}
            isLable={" Return Sale " + isDeleteData?.saleReturnNo}
            isBtnLoading={isBtnLoading}
          />
        )} */}
        {/* {isAddModalOpen && (
          <AddSale
            isActive={isActive}
            onClose={handleAddClose}
            // onAdd={handleAddData}
          />
        )} */}

        {/* {isAddModalOpen && (
          <AddPurchase onClose={handleAddClose} handleAdd={handleAddPurchase} />
        )} */}
      </div>
    </div>
  );
};

export default ListReturnSale;
